import { useState } from "react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faEye,
  faArrowUp,
  faArrowDown,
  faChartPie,
  faDollarSign,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import SearchBar from "../../../components/ui/SearchBar";
import TableWrapper from "../../../components/ui/TableWrapper";
import EmptyState from "../../../components/ui/EmptyState";
import Pagination from "../../../components/ui/Pagination";

// ─── Main Transaction View Component ──────────────────────────────────────

const TransactionView = ({
  transactions,
  formatCurrency,
  formatDate,
  getCategoryColor,
  searchTerm,
  setSearchTerm,
  categoryFilter,
  setCategoryFilter,
  typeFilter,
  setTypeFilter,
  categories,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  totalItems,
  totalPages,
  currentData,
  startIndex,
  summary,
  setSelectedTransaction,
  setViewModalOpen,
  setEditModalOpen,
  canUpdate = false,
}) => {
  const [showTable, setShowTable] = useState(false);

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Card className="bg-white border border-green-200 p-3 text-center">
          <p className="text-xs uppercase tracking-wider text-green-600 font-semibold">
            <FontAwesomeIcon icon={faArrowUp} className="mr-1 h-3 w-3" />
            Total Money IN
          </p>
          <p className="text-lg font-bold text-green-700">
            {formatCurrency(summary.totalIn)}
          </p>
        </Card>
        <Card className="bg-white border border-red-200 p-3 text-center">
          <p className="text-xs uppercase tracking-wider text-red-600 font-semibold">
            <FontAwesomeIcon icon={faArrowDown} className="mr-1 h-3 w-3" />
            Total Money OUT
          </p>
          <p className="text-lg font-bold text-red-700">
            {formatCurrency(summary.totalOut)}
          </p>
        </Card>
        <Card
          className={`bg-white border-2 p-3 text-center ${
            summary.netBalance >= 0
              ? "border-green-400 bg-green-50"
              : "border-red-400 bg-red-50"
          }`}
        >
          <p className="text-xs uppercase tracking-wider font-semibold">
            <FontAwesomeIcon icon={faDollarSign} className="mr-1 h-3 w-3" />
            Net Balance
          </p>
          <p
            className={`text-lg font-bold ${
              summary.netBalance >= 0 ? "text-green-700" : "text-red-700"
            }`}
          >
            {formatCurrency(summary.netBalance)}
          </p>
        </Card>
        <Card className="bg-white border border-blue-200 p-3 text-center">
          <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold">
            <FontAwesomeIcon icon={faChartPie} className="mr-1 h-3 w-3" />
            Transactions
          </p>
          <p className="text-lg font-bold text-blue-700">
            {summary.totalTransactions}
          </p>
        </Card>
      </div>

      {/* Breakdown by Category */}
      <Card className="bg-white border border-gray-200 p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <FontAwesomeIcon
            icon={faChartPie}
            className="text-blue-600 h-4 w-4"
          />
          Breakdown by category
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {Object.entries(summary.breakdown).map(([category, amounts]) => (
            <div
              key={category}
              className={`flex items-center justify-between p-2 rounded-sm border px-3 ${getCategoryColor(category)}`}
            >
              <span className="text-sm font-medium truncate text-white">
                {category}
              </span>
              <div className="flex items-center gap-1 text-sm">
                {amounts.in > 0 && (
                  <span className="text-white font-semibold">
                    +{formatCurrency(amounts.in)}
                  </span>
                )}
                {amounts.out > 0 && amounts.in > 0 && (
                  <span className="text-white/30 mx-0.5">|</span>
                )}
                {amounts.out > 0 && (
                  <span className="text-white font-semibold">
                    -{formatCurrency(amounts.out)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* View More Button */}
      <div className="flex justify-center">
        <Button
          onClick={() => setShowTable(!showTable)}
          className="gap-2 bg-blue-800 hover:bg-blue-900 text-white px-6"
        >
          <FontAwesomeIcon icon={showTable ? faChevronUp : faChevronDown} />
          {showTable ? "Hide details" : "View more"}
        </Button>
      </div>

      {/* Table - Hidden until View More is clicked */}
      {showTable && (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search by description or category..."
              className="flex-1"
            />

            <div className="flex flex-wrap items-center gap-2">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-40 h-9 border border-gray-300 rounded-lg bg-white text-sm">
                  <div className="flex items-center gap-2">
                    <SelectValue placeholder="All Categories" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-32 h-9 border border-gray-300 rounded-lg bg-white text-sm">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="IN">Money IN</SelectItem>
                  <SelectItem value="OUT">Money OUT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table */}
          <TableWrapper
            title="transactions"
            totalItems={totalItems}
            currentPage={currentPage}
            totalPages={totalPages}
            className="bg-white border border-gray-200 shadow-sm"
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    #
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Date
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Description
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4 hidden md:table-cell">
                    Category
                  </th>
                  <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Type
                  </th>
                  <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Amount
                  </th>
                  <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {currentData.length > 0 ? (
                  currentData.map((item, i) => {
                    const globalIndex = startIndex + i + 1;
                    const isEditable =
                      item.category === "Expense" ||
                      item.category === "Receive";
                    const showEdit = canUpdate && isEditable;

                    return (
                      <tr
                        key={item.id}
                        className="hover:bg-blue-50/50 transition-colors"
                      >
                        <td className="py-3 px-4 text-xs text-gray-400 font-medium">
                          {globalIndex}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {formatDate(item.date)}
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-800 text-sm">
                          {item.description}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 hidden md:table-cell">
                          <Badge
                            variant="outline"
                            className={`${getCategoryColor(item.category)} text-xs font-normal px-2 py-1 rounded-full text-white`}
                          >
                            {item.category}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge
                            variant="outline"
                            className={`${
                              item.type === "IN"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-red-100 text-red-700 border-red-200"
                            } text-xs font-normal px-2 py-1 rounded-full`}
                          >
                            <FontAwesomeIcon
                              icon={
                                item.type === "IN" ? faArrowUp : faArrowDown
                              }
                              className="mr-1 h-3 w-3"
                            />
                            {item.type}
                          </Badge>
                        </td>
                        <td
                          className={`py-3 px-4 text-right font-semibold text-sm ${
                            item.type === "IN"
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {item.type === "IN" ? "+" : "-"}
                          {formatCurrency(item.amount)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50 hover:text-blue-700 rounded-full"
                              onClick={() => {
                                setSelectedTransaction(item);
                                setViewModalOpen(true);
                              }}
                              title="View Details"
                            >
                              <FontAwesomeIcon
                                icon={faEye}
                                className="h-4 w-4"
                              />
                            </Button>

                            {showEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-green-600 hover:bg-green-50 hover:text-green-700 rounded-full"
                                onClick={() => {
                                  setSelectedTransaction(item);
                                  setEditModalOpen(true);
                                }}
                                title="Edit Transaction"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="h-4 w-4"
                                />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-8">
                      <EmptyState
                        title="No transactions found"
                        message={
                          searchTerm ||
                          categoryFilter !== "all" ||
                          typeFilter !== "all"
                            ? "Try adjusting your search or filters"
                            : "Add your first transaction to get started"
                        }
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableWrapper>

          {/* Pagination */}
          {totalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </>
      )}
    </>
  );
};

export default TransactionView;
